// Vollea — Organiser score override modal. Opens from a fixture card, lets
// the organiser force-set the final set scores on ANY match (scheduled,
// pending, disputed, or already-confirmed — the backend reverts prior
// standings before re-applying to keep counters balanced).
import React, { useState } from "react";
import { IoClose, IoSaveOutline } from "react-icons/io5";
import { api } from "../lib/api";
import { C, SP, R } from "../lib/theme";

export default function OrganiserScoreModal({ match, onClose, onSaved }) {
  const initial = (match?.sets || []).length
    ? match.sets.map((s) => ({ a: String(s.team_a ?? s.a ?? ""), b: String(s.team_b ?? s.b ?? "") }))
    : [{ a: "", b: "" }, { a: "", b: "" }, { a: "", b: "" }];
  const [sets, setSets] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const parsed = sets
        .map((s) => ({ team_a: parseInt(s.a, 10), team_b: parseInt(s.b, 10) }))
        .filter((s) => Number.isFinite(s.team_a) && Number.isFinite(s.team_b));
      if (parsed.length === 0) throw new Error("Enter at least one set");
      const updated = await api.post(`/matches/${match.match_id}/organiser-set-score`, { sets: parsed });
      onSaved?.(updated);
      onClose?.();
    } catch (e) { setErr(e.message || "Save failed"); }
    finally { setBusy(false); }
  };

  const teamAName = (match.team_a_players || []).map((p) => p.name).join(" & ") || match.team_a_name || "Team A";
  const teamBName = (match.team_b_players || []).map((p) => p.name).join(" & ") || match.team_b_name || "Team B";
  const wasConfirmed = match.status === "confirmed";

  return (
    <div data-testid="organiser-score-modal" style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: `${SP.md}px ${SP.lg}px`, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Override score</div>
          <button data-testid="organiser-score-close" onClick={onClose} style={{ background: "transparent", border: "none", color: C.text, cursor: "pointer" }}><IoClose size={22} /></button>
        </div>

        <div style={{ padding: SP.lg }}>
          {wasConfirmed && (
            <div style={{ background: "rgba(245,158,11,0.1)", border: `1px solid ${C.warning}`, borderRadius: R.sm, padding: SP.md, color: C.warning, fontSize: 12, marginBottom: SP.md }}>
              This match is already confirmed. Saving a new score will revert the previous standings impact and apply the corrected one.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr", gap: SP.sm, alignItems: "center", marginBottom: SP.md }}>
            <div data-testid="score-team-a-name" style={{ color: C.text, fontSize: 13, fontWeight: 700, textAlign: "right" }}>{teamAName}</div>
            <div style={{ color: C.textDim, fontSize: 11, letterSpacing: 1, textAlign: "center", fontWeight: 800 }}>VS</div>
            <div data-testid="score-team-b-name" style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{teamBName}</div>
          </div>

          {sets.map((s, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr", gap: SP.sm, alignItems: "center", marginTop: SP.sm }}>
              <input
                inputMode="numeric"
                value={s.a}
                onChange={(e) => setSets(sets.map((x, j) => j === i ? { ...x, a: e.target.value.replace(/[^0-9]/g, "") } : x))}
                data-testid={`score-set-a-${i}`}
                style={inputStyle}
              />
              <div style={{ color: C.textMuted, fontSize: 11, letterSpacing: 1, textAlign: "center", fontWeight: 700 }}>SET {i + 1}</div>
              <input
                inputMode="numeric"
                value={s.b}
                onChange={(e) => setSets(sets.map((x, j) => j === i ? { ...x, b: e.target.value.replace(/[^0-9]/g, "") } : x))}
                data-testid={`score-set-b-${i}`}
                style={inputStyle}
              />
            </div>
          ))}

          {err ? <div data-testid="organiser-score-err" style={{ color: C.error, fontSize: 12, marginTop: SP.md }}>{err}</div> : null}

          <button type="button" data-testid="organiser-score-save" onClick={submit} disabled={busy} style={{ marginTop: SP.lg, width: "100%", height: 48, borderRadius: R.md, background: C.lime, color: C.onLime, fontWeight: 800, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <IoSaveOutline size={16} /> Save score
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 95, display: "flex", alignItems: "center", justifyContent: "center", padding: SP.lg };
const sheet = { width: "100%", maxWidth: 460, background: C.bg, borderRadius: R.lg, border: `1px solid ${C.border}` };
const inputStyle = { width: "100%", height: 48, background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.md, color: C.text, textAlign: "center", fontSize: 20, fontWeight: 700, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
